import type { PaymentProblemKind, ReadinessEscalation } from '@sigora/domain';

import type { AnalysingStage } from './fielding-stage.ts';

/**
 * What the Launch step says about the study it is about to buy and the price it is bought at, and
 * what the Fielding and Analyzing steps say once it is bought: one module, so the product copy is
 * one file to change.
 */

/** The labels of the four rows that say what is being bought. */
export const SUMMARY_LABELS = {
  type: 'Type',
  goal: 'Goal',
  guide: 'Guide',
  audience: 'Audience',
} as const;

/** A row whose read is still out. */
export const SUMMARY_ROW_PENDING = 'Reading…';

/** A row whose read failed. The price's "Try again" reads it again. */
export const SUMMARY_ROW_FAILED = 'Couldn’t be read just now.';

/** The Audience row: how many people, and where they live. */
export const audienceSummary = (completes: number, countries: readonly string[]): string =>
  `${String(completes)} ${completes === 1 ? 'person' : 'people'} in ${countries.join(' and ')}`;

/** While a price is being asked for, the first time or again. */
export const PRICE_LOADING = 'Working out the price…';

/** While the price shown has reached the end of its window and a new one is being asked for. */
export const PRICE_RECHECKING = 'This price’s window has ended. Checking the price again…';

/** While the price shown is asked for again after the study may have changed under it. */
export const PRICE_UPDATING = 'Checking the price is still current…';

/** No price could be had. Nothing is said about an amount, because there is none to say. */
export const PRICE_FAILED =
  'The price could not be worked out just now. Try again — nothing has changed.';

export const PRICE_RETRY = 'Try again';

/**
 * The minutes the price was worked out on. They are more than the Guide row's, since the price
 * adds the screening questions, so the line says so rather than leaving the gap unexplained.
 */
export const pricedMinutesLine = (minutes: number): string =>
  `Priced on about ${String(minutes)} ${minutes === 1 ? 'minute' : 'minutes'} per person, including screening.`;

/** How long a live price holds, after its age. */
export const priceHoldsUntil = (time: string): string => `This price holds until ${time}.`;

export const PRICE_ALL_IN = 'Everything is included in this price.';

/** Under a price that replaced the one shown before it while the screen was open. */
export const priceChangedLine = (previous: string): string =>
  `The price changed since it was last worked out. It was ${previous}.`;

/**
 * Under the price a purchase already holds the study at. The date is when that price was worked
 * out, which is what the quote carries; the price no longer lapses.
 */
export const priceAcceptedLine = (workedOutOn: string): string =>
  `You accepted this price. It was worked out on ${workedOutOn}.`;

/**
 * The hero over a live study's progress bar, and the card's one count line: how many have finished,
 * out of how many where the study has a target.
 */
export const progressHeroLabel = (completed: number, target: number | null): string =>
  target === null
    ? `${String(completed)} finished`
    : `${String(completed)} of ${String(target)} finished`;

/**
 * The headline of an analysing study before its Brief request has been read, and over a Brief that
 * failed or was declined: true of every analysing study, so it claims nothing a later read could
 * take back.
 */
export const FIELDWORK_FINISHED = 'Fieldwork is finished.';

/** The headline of an analysing study, by its stage, until the server stops waiting for it. */
export const ANALYSING_HEADLINES: Record<AnalysingStage, string> = {
  reading: 'Sigora is reading the answers now.',
  brief_next: 'Your brief is next.',
  // Over the failure or decline line the synthesis status shows, which says what went wrong.
  brief_failed: FIELDWORK_FINISHED,
  declined: FIELDWORK_FINISHED,
};

/**
 * The headline of a `live` study once everyone it asked for has finished, until the server moves it
 * on to `analyzing`. Says nothing about reading the answers, which the closing line under the card
 * says is next.
 */
export const FIELDING_AT_TARGET_HEADLINE = 'This study has all the participants it asked for.';

/**
 * Under a `live` study's progress card once everyone it asked for has finished, beside a spinner,
 * until the server moves the study on to `analyzing`.
 */
export const FIELDWORK_CLOSING_LINE = 'Fieldwork is closing. Sigora reads the answers next.';

/** Above the research questions on the Analyzing step while the answers are read. */
export const RESEARCH_QUESTIONS_LEAD = 'Sigora reads the answers against your research questions:';

/**
 * Launch on a launched study: when it launched, and that it was paid for when a purchase holds it.
 * The date is the reader's own calendar date.
 */
export const launchedSummaryLine = (launchedOn: string, paid: boolean): string =>
  paid ? `Paid and launched on ${launchedOn}.` : `Launched on ${launchedOn}.`;

/** Launch on a launched study whose launch time the read does not carry. */
export const LAUNCHED_UNDATED = 'This study has launched.';

/** Beside Next on the Fielding step while the study is live. */
export const FIELDING_CONTINUE_HINT = 'Next opens once fieldwork is finished.';

/** Beside Next on the Analyzing step while it waits on the Brief. */
export const ANALYZING_CONTINUE_HINT = 'Next opens once your brief is written.';

/**
 * The Analyzing step of a study whose Brief is written: what happened there, with nothing still in
 * progress. The footer's Next is the way on to the Brief.
 */
export const ANALYZING_DONE_LINE = 'Sigora has read the answers and written your brief.';

/**
 * What an `analyzing` study says once the poller has stopped waiting for its analysis.
 *
 * The analysing headlines — "Sigora is reading the answers now" — stop being true once the poller
 * has given up, and the study keeps that state because there is no later one for it to move to.
 * Left alone the step would repeat a claim the server already knows is false, which is the one
 * thing a prototype still may not do. Neither message offers a repair, because there is none to
 * offer: what they replace is a promise, not a control. Remesh's own reason for the ending is not
 * shown: it is a vendor code, kept on the study read, in the worker's log and in the agent's
 * digest for whoever has to look into it.
 */
export const READINESS_ESCALATION_MESSAGES: Record<ReadinessEscalation, string> = {
  analysis_unavailable:
    'Fieldwork is finished, but its answers cannot be read: the analysis of this study did not complete. Nothing further will happen on its own.',
  timed_out:
    'Fieldwork is finished, but its answers are still not ready, and this study has stopped being watched. Nothing further will happen on its own.',
};

/** How long fielding takes. Static: it states no prediction for this study. */
export const FIELDING_TIME =
  "Most studies finish within a few hours to a few days, depending on the audience. We'll show progress here once it starts.";

/**
 * Under the refusal of an N out of range, when the refusal names the largest a study may ask for.
 * The refusal's own sentence is the server's (`QUOTE_REFUSAL_MESSAGES` in `@sigora/domain`), which
 * Launch shows as it is; this is the one line Launch adds to it.
 */
export const mostPeopleLine = (most: number): string =>
  `The most a study can ask for is ${String(most)} people.`;

/** Under an N out of range: the step where the number is changed. */
export const OPEN_AUDIENCE_STEP = 'Open the Audience step';

/** Pay, and while its request is out. */
export const PAY_LABEL = 'Pay and launch';
export const PAYING_LABEL = 'Paying…';

/** Above every step while a purchase holds the study's documents and its checkout is open. */
export const STEP_HELD_BY_CHECKOUT =
  'Checkout is open for this study, so nothing on this step can change until it is paid for or the checkout is cancelled.';

/** The price a pending purchase holds the study at, beside the checkout it is open for. */
export const checkoutAmountLine = (amount: string): string =>
  `${amount}, the price you accepted. Everything is included in this price.`;

/** A pending purchase whose checkout session is open, on a panel with no payment form to show. */
export const CHECKOUT_OPEN_NO_FORM =
  'Checkout is open for this study. Nothing can be changed until it is paid for or the checkout is cancelled.';

/** A pending purchase whose checkout session is open, above its payment form. */
export const CHECKOUT_OPEN =
  'Checkout is open for this study. Nothing can be changed until it is paid for or cancelled, and any problem with a card shows in the payment form.';

/** A pending purchase whose checkout session has not been opened yet. */
export const CHECKOUT_OPENING =
  'Checkout is still being opened for this study. Nothing has been charged. Try again in a moment.';

/** Answers the opening alert: asks for the checkout again. */
export const CHECKOUT_RETRY = 'Try again';

/** Cancels the pending checkout, and while its request is out. */
export const CANCEL_CHECKOUT = 'Cancel checkout';
export const CANCELLING_CHECKOUT = 'Cancelling…';

/** Opens the payment form for a checkout this page did not open itself, and while it is asked for. */
export const CONTINUE_CHECKOUT = 'Continue to payment';
export const CONTINUING_CHECKOUT = 'Opening the payment form…';

/** The heading of the modal the payment form is shown in. */
export const CHECKOUT_MODAL_TITLE = 'Pay for your study';

/** Shows the payment form again once its modal has been closed; the checkout stays open. */
export const SHOW_CHECKOUT_FORM = 'Show the payment form';

/** While Stripe's payment form is loaded into the page. */
export const CHECKOUT_FORM_LOADING = 'Loading the payment form…';

/** The payment form could not be loaded or opened; Try again loads it again. */
export const CHECKOUT_FORM_FAILED =
  "The payment form couldn't be loaded. Nothing has been charged. Try again, or cancel the checkout.";

/** While the server confirms a payment the payment form reported complete. */
export const PAYMENT_CONFIRMING =
  'Confirming your payment. This takes a moment — there is no need to pay again.';

/** Added once the quick confirmation checks have run out with the payment still unconfirmed. */
export const PAYMENT_CONFIRMING_SLOW =
  'This is taking longer than usual. The page keeps checking, and the payment will not be taken twice.';

/** Asks once more about a payment whose quick checks have run out, and while that ask is out. */
export const CHECK_PAYMENT_AGAIN = 'Check again';
export const CHECKING_PAYMENT = 'Checking…';

/** Continue, while the provider is asked whether the open checkout was already paid. */
export const CHECKING_CHECKOUT = 'Checking the payment…';

/** In place of the payment form, where the server answered no publishable key outside a dev build. */
export const CHECKOUT_NOT_CONFIGURED = "Card payments aren't set up on this server.";

/** The test checkout once Pay or Expire is sent, until the server settles the checkout. */
export const TEST_CHECKOUT_WAITING =
  'Sent. Waiting for the payment provider to settle this checkout…';

/** The local-only stand-in for the payment form, on a stack running the fake provider. */
export const TEST_CHECKOUT_HEADING = 'Test checkout';
export const TEST_CHECKOUT_LINE =
  'This local stack takes no card. Pay or expire this checkout here, as the payment provider would; nothing is charged.';
export const TEST_CHECKOUT_PAY = 'Pay';
export const TEST_CHECKOUT_EXPIRE = 'Expire';
export const TEST_CHECKOUT_FAILED = "The test checkout couldn't be sent. Try again.";

/** Once a pending checkout has been watched for as long as a session stays open. */
export const CHECKOUT_WATCH_STOPPED =
  'This page has stopped checking on the checkout. Reload it to see whether the payment went through.';

/** A payment answered as taken, before the next read of the study shows its set-up. */
export const PAYMENT_TAKEN = 'Payment taken. Setting your study up now.';

/** A cancellation that closed the checkout. */
export const CHECKOUT_CANCELLED =
  'Checkout cancelled. Nothing was charged, and the study can be changed again.';

/** A cancellation the provider answered with a payment that had already gone through. */
export const PAID_BEFORE_CANCEL =
  'The payment went through before the checkout could be cancelled, so your study is being set up.';

/** Above the price, after the study's latest checkout closed without a payment. */
export const closedCheckoutLine = (state: 'cancelled' | 'expired'): string =>
  state === 'cancelled'
    ? 'Your last checkout was cancelled before it was paid. Here is the price now; paying needs another press of Pay.'
    : 'Your last checkout closed before it was paid. Here is the price now; paying needs another press of Pay.';

/** A setup restart that was sent. */
export const SETUP_RESTARTED =
  'Restarted. This study was already paid for, so nothing has been charged again.';

/** Starts a paid study's stopped setup again, and while its request is out. */
export const RETRY_SETUP = 'Try again — no new payment is needed';
export const RETRYING_SETUP = 'Trying again…';

/** After a stop's own sentence: what the stop did not do. */
export const NOT_CHARGED_TWICE = 'Nothing has been charged twice.';

/** The heading over the payment problems a study holds. */
export const PAYMENT_PROBLEMS_HEADING = 'About a payment';

/**
 * One sentence per kind of payment problem, said to the researcher who owns the study. Every
 * payment that could not be used is refunded by hand, and none of them asks anything of the
 * researcher.
 */
export const PAYMENT_PROBLEM_COPY: Record<PaymentProblemKind, string> = {
  paid_after_close:
    'A payment was received for a checkout that had already closed. It will be refunded, and nothing is needed from you.',
  evidence_mismatch:
    "A payment was received that didn't match this study's checkout, so it was not used. It will be refunded, and nothing is needed from you.",
  unknown_purchase:
    'A payment was received for a checkout this study no longer has. It will be refunded, and nothing is needed from you.',
  refunded:
    "This study's payment was refunded, so it will not be set up. Nothing is needed from you.",
  unexpected_session_state:
    'A checkout for this study finished without a payment that could be confirmed. It is being looked into, and nothing is needed from you.',
};

/** When a payment problem was recorded, after its sentence. */
export const problemRecordedLine = (when: string): string => `Recorded ${when}.`;
